import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { queryKeys } from "@/lib/query-keys"
import { ProjectService } from "@/services/project-service"
import { useProjectFileSelectionStore } from "@/stores/project-file-selection-store"

type BulkDeleteParams = {
  projectId: string
  filenames: string[]
}

export function useBulkDeleteProjectFiles() {
  const queryClient = useQueryClient()
  const { t } = useTranslation("", { keyPrefix: "project_settings.files" })
  const { setDeletionProgress, reset } = useProjectFileSelectionStore()

  return useMutation({
    onMutate: ({ filenames }) => {
      setDeletionProgress({ total: filenames.length, completed: 0, failed: [] })
    },
    mutationFn: async ({ projectId, filenames }: BulkDeleteParams) => {
      const failed: string[] = []
      const total = filenames.length

      // Process files sequentially to avoid server overload
      for (let index = 0; index < filenames.length; index++) {
        const filename = filenames[index]
        try {
          await ProjectService.deleteFile(projectId, filename)
        } catch {
          failed.push(filename)
        }
        setDeletionProgress({ total, completed: index + 1, failed })
      }

      return { deleted: total - failed.length, failed, total }
    },
    onSuccess: (result, { projectId }) => {
      // Invalidate queries to refresh file list
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectFiles(projectId) })

      // Show appropriate toast based on results
      if (result.failed.length === 0) {
        toast.success(t("files_deleted", { count: result.deleted }))
      } else if (result.deleted > 0) {
        toast.warning(
          t("files_deleted_partial", { deleted: result.deleted, failed: result.failed.length })
        )
      } else {
        toast.error(t("files_delete_failed"))
      }

      // Reset store state
      reset()
    },
    onError: () => {
      toast.error(t("files_delete_failed"))
      reset()
    },
  })
}
